<template>
  <div class="quote-calculation" v-if="productGroup && petTypes">
    <FilterComponent
      v-if="!props.hideFilter && selectedProductGroupId !== PRODUCT_GROUP.HPS"
      v-model:selectedPetType="selectedPetType"
      v-model:selectedBreed="selectedBreed"
      v-model:selectedDate="selectedDate"
      v-model:minDate="minDate"
      v-model:maxDate="maxDate"
      :showCombi="tksOpsCombi"
      :showSpecificPetType="showSpecificPetType"
      :petTypes="petTypes"
      @showTariffs="showTariffs($event)"
    ></FilterComponent>

    <template v-if="!tksOpsCombi || hideCombiFilter">
      <HeadlineComponent
        :petName="props.petName"
        :liability="props.liability"
        :productGroup="productGroup"
        :selectedPetType="selectedPetType"
      ></HeadlineComponent>

      <TableHeadComponent
        :product="product"
        :selectedDate="selectedDate"
        :liability="props.liability"
        :defaultProduct="defaultProduct"
        :highlightDefaultProduct="highlightDefaultProduct"
        :productGroup="productGroup"
        @selectProduct="selectProduct($event)"
      ></TableHeadComponent>

      <TableContentComponent
        :productGroup="productGroup"
        :highlightDefaultProduct="highlightDefaultProduct"
        :defaultProduct="defaultProduct"
        :liability="props.liability"
      ></TableContentComponent>

      <div class="table-head-bottom" v-if="props.showBottomButtons">
        <div
          v-for="(product, index) in productGroup.products"
          :key="index"
          class="table-head-column"
        >
          <TableColumnHeadComponent
            :product="product"
            :selectedDate="selectedDate"
            :liability="props.liability"
            :defaultProduct="defaultProduct"
            :forceMobile="true"
            :index="index"
            @selectProduct="selectProduct(product)"
          ></TableColumnHeadComponent>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { defineProps, withDefaults, defineEmits, ref, watch } from "vue";
import productsApi from "../api/products";
import { PET_TYPE, PRODUCT_GROUP } from "../store/variables";
import { ProductGroup } from "../classes/productGroup";
import { Product } from "../classes/product";
import { PetType } from "../classes/petType";
import { Breed } from "../classes/breed";
import FilterComponent from "./sections/FilterComponent.vue";
import TableColumnHeadComponent from "./sections/TableColumnHeadComponent.vue";
import HeadlineComponent from "./sections/HeadlineComponent.vue";
import TableHeadComponent from "./sections/TableHeadComponent.vue";
import TableContentComponent from "./sections/TableContentComponent.vue";
import { trackObject, trackEvent, trackInitial } from "../helper/tracking";
import { getSessionObject, setSessionEntry } from "../helper/session";
import { getFormattedDate, getFormattedParamDate } from "../helper/general";
import { useFocusVisible } from "frontend-component-templates/src/composables/useFocusVisible";
import { useZoomSticky } from "frontend-component-templates/src/composables/useZoomSticky";

interface Props {
  standalone?: boolean;
  productGroup?: number;
  petType?: number;
  petBirthday?: Date;
  petBreed?: number;
  petBreedIdentifier?: number;
  petName?: string;
  hideFilter?: boolean;
  liability?: boolean;
  sticky?: boolean;
  petTypes?: any;
  productGroupObject?: any;
  showBottomButtons?: boolean;
  tracking?: boolean;
  excludeProduct?: string;
  startDate?: Date;
  showHiddenRates?: boolean;
  baseUrl?: string;
  xApiKey?: string;
  clientKey?: string;
}

const props = withDefaults(defineProps<Props>(), {
  standalone: true,
  productGroup: undefined,
  petType: PET_TYPE.DOG,
  petBirthday: null,
  petBreed: null,
  petBreedIdentifier: null,
  petName: "",
  hideFilter: false,
  liability: false,
  sticky: true,
  petTypes: null,
  productGroupObject: null,
  showBottomButtons: false,
  tracking: true,
  excludeProduct: null,
  startDate: null,
  showHiddenRates: false,
  baseUrl: null,
  xApiKey: null,
  clientKey: null,
});

const emit = defineEmits<{
  (e: "selectProduct", product: Product): void;
  (e: "loadingComplete"): void;
}>();

productsApi.setApiKeys(props.baseUrl, props.xApiKey, props.clientKey);

const productGroup = ref<ProductGroup>();
const petTypes = ref<PetType[]>(
  props.petTypes || (await productsApi.getPetTypes())
);
const selectedDate = ref<Date>(
  props.petBirthday ? new Date(props.petBirthday) : undefined
);
const selectedPetType = ref<PetType>();
const selectedBreed = ref<Breed>();
const selectedProductGroupId = ref<number>(parseInt(props.productGroup));
const maxDate = ref<string>();
const minDate = ref<string>();
const showSpecificPetType = ref<number>();
const defaultProduct = ref<number>(2);
const highlightDefaultProduct = ref<boolean>(false);
const hideCombiFilter = ref(false);
const tksOpsCombi = ref<boolean>(false);
const excludeProduct = ref(props.excludeProduct);

checkDataAttributes();
checkSessionStorage();
useFocusVisible();
useZoomSticky();

function checkDataAttributes() {
  const quoteCalculationElement = document.getElementById("quote-calculation");
  if (quoteCalculationElement) {
    const dataProductGroup =
      quoteCalculationElement.getAttribute("data-product-group");
    const dataPetType = quoteCalculationElement.getAttribute("data-pet-type");
    const dataSelectedPetType = quoteCalculationElement.getAttribute(
      "data-selected-pet-type"
    );
    const dataDefaultProduct = quoteCalculationElement.getAttribute(
      "data-default-product"
    );
    const dataCombi = quoteCalculationElement.getAttribute("data-combi");
    const dataTksOps = quoteCalculationElement.getAttribute("data-tks-ops");

    if (!props.productGroup) {
      selectedProductGroupId.value =
        dataProductGroup === "OPS"
          ? PRODUCT_GROUP.OPS
          : dataProductGroup === "HPS"
          ? PRODUCT_GROUP.HPS
          : PRODUCT_GROUP.TKS;
    }

    if (dataPetType === "dog") {
      showSpecificPetType.value = PET_TYPE.DOG;
    } else if (dataPetType === "cat") {
      showSpecificPetType.value = PET_TYPE.CAT;
    }

    if (dataSelectedPetType) {
      setSelectedPetType(
        petTypes.value.find(
          (apiPetType) => apiPetType.ident === dataSelectedPetType
        )
      );
    }
    if (dataDefaultProduct) {
      defaultProduct.value = Number(dataDefaultProduct);
      highlightDefaultProduct.value = true;
    }
    if (dataCombi) {
      excludeProduct.value = "HPS24";
    }
    if (dataTksOps) {
      tksOpsCombi.value = true;
    }
  }
}

function checkSessionStorage() {
  if (sessionStorage.getItem("quoteCalculation")) {
    const sessionObject = getSessionObject();
    if (sessionObject.productGroupId === selectedProductGroupId.value) {
      if (
        sessionObject.petTypeId &&
        !document
          .getElementById("quote-calculation")
          ?.getAttribute("data-selected-pet-type")
      ) {
        setSelectedPetType(
          petTypes.value.find(
            (apiPetType) => apiPetType.id === sessionObject.petTypeId
          )
        );
      }
      if (sessionObject.breedId) {
        selectedBreed.value = selectedPetType.value?.breeds.find(
          (apiBreed) => apiBreed.identifier === sessionObject.breedId
        );
      }
      if (sessionObject.birthday) {
        selectedDate.value = new Date(sessionObject.birthday);
      }
    } else {
      setSelectedPetType(petTypes.value[0]);
      sessionStorage.removeItem("quoteCalculation");
    }
  } else if (
    !document
      .getElementById("quote-calculation")
      ?.getAttribute("data-selected-pet-type")
  ) {
    setSelectedPetType(petTypes.value[0]);
  }
}

if (props.petType) {
  setSelectedPetType(
    petTypes.value.find((apiPetType) => apiPetType.id === props.petType)
  );
}
if (props.petBreed) {
  selectedBreed.value = selectedPetType.value?.breeds.find(
    (apiBreed) => apiBreed.id === props.petBreed
  );
}
if (props.petBreedIdentifier) {
  selectedBreed.value = selectedPetType.value?.breeds.find(
    (apiBreed) => apiBreed.identifier === props.petBreedIdentifier
  );
}
await getProductGroup(
  "tierart",
  selectedPetType.value?.name,
  props.productGroupObject
);
emit("loadingComplete");
setSessionEntry("petTypeId", selectedPetType.value?.id);
setSessionEntry("productGroupId", selectedProductGroupId.value);

async function showTariffs(selectedProductGroupId: number): void {
  hideCombiFilter.value = true;
  productGroup.value = await productsApi.getProductGroup(
    selectedProductGroupId,
    selectedPetType.value?.id,
    selectedBreed.value?.identifier,
    getFormattedDate(selectedDate.value),
    props.startDate
      ? new Date(props.startDate).toLocaleDateString("de-DE")
      : undefined,
    props.showHiddenRates
  );
  checkExcludedProduct();
  minDate.value = productGroup.value?.minDate;
  maxDate.value = productGroup.value?.maxDate;
  if (props.tracking) {
    trackObject(
      "view_item_list",
      productGroup.value?.products,
      selectedPetType.value,
      selectedBreed.value,
      getFormattedDate(selectedDate.value)
    );
  }
  setTimeout(() => {
    document
      .getElementsByClassName("quote-name")[0]
      .scrollIntoView({ behavior: "smooth" });
  });
}

function checkExcludedProduct() {
  if (excludeProduct.value) {
    const excludedProduct = productGroup.value.products.find(
      (item) => item.identifier === excludeProduct.value
    );
    if (excludedProduct) {
      productGroup.value.products = productGroup.value.products.filter(
        (item) =>
          item.identifier !== excludeProduct.value &&
          excludedProduct?.downGradeOptions?.indexOf(item.id) > -1
      );
    }
  }
}

function setSelectedPetType(petType: PetType): void {
  if (showSpecificPetType.value) {
    selectedPetType.value = petTypes.value.find(
      (petType) => petType.id === showSpecificPetType.value
    );
  } else {
    selectedPetType.value = petType;
  }
}

function selectProduct(product: Product): void {
  if (props.tracking) {
    trackObject(
      "select_item",
      [product],
      selectedPetType.value,
      selectedBreed.value,
      getFormattedDate(selectedDate.value)
    );
  }
  if (props.standalone) {
    const params = new URLSearchParams();
    params.set("pet", selectedPetType.value?.id);
    params.set("productGroup", selectedProductGroupId.value);
    params.set("product", product.id);
    if (selectedBreed.value?.id) {
      params.set("breed", selectedBreed.value.identifier);
    }
    if (selectedDate.value) {
      params.set("birthday", getFormattedParamDate(selectedDate.value));
    }
    if (window.self !== window.top) {
      params.set("brokerID", "50000005");
    }

    const targetUrl =
      process.env.VUE_APP_ORIGIN + "antrag2/#/1/tier/typ?" + params.toString();
    if (window.self !== window.top) {
      window.open(targetUrl);
    } else {
      window.location.href = targetUrl;
    }
  } else {
    emit("selectProduct", product);
  }
}

async function getProductGroup(
  trackingName?: string,
  trackingValue?: string | undefined,
  propProductGroup?: ProductGroup
): void {
  let productGroupTemp;
  if (propProductGroup) {
    productGroupTemp = new ProductGroup(propProductGroup);
  } else {
    productGroupTemp = await productsApi.getProductGroup(
      selectedProductGroupId.value,
      selectedPetType.value?.id,
      selectedBreed.value?.identifier,
      getFormattedDate(selectedDate.value),
      props.startDate
        ? new Date(props.startDate).toLocaleDateString("de-DE")
        : undefined,
      props.showHiddenRates
    );
  }
  if (props.liability) {
    productGroupTemp.products = productGroupTemp.products.slice(1);
    productGroup.value = productGroupTemp;
  } else {
    productGroup.value = productGroupTemp;
  }
  checkExcludedProduct();
  minDate.value = productGroup.value?.minDate;
  maxDate.value = productGroup.value?.maxDate;
  if (trackingName && trackingValue && props.tracking) {
    trackEvent(trackingName, trackingValue);
  }
  if (props.tracking) {
    trackObject(
      "view_item_list",
      productGroup.value?.products,
      selectedPetType.value,
      selectedBreed.value,
      getFormattedDate(selectedDate.value)
    );
  }
}

watch(
  () => selectedPetType.value,
  () => {
    selectedBreed.value = undefined;
    if (!tksOpsCombi.value) {
      getProductGroup("tierart", selectedPetType.value?.name);
      setSessionEntry("petTypeId", selectedPetType.value?.id);
    }
  }
);
watch(
  () => selectedBreed.value,
  () => {
    setSessionEntry("breedId", selectedBreed.value?.identifier);
    if (!tksOpsCombi.value) {
      getProductGroup("rasse", selectedBreed.value?.name);
    }
  }
);
watch(
  () => selectedDate.value,
  () => {
    setSessionEntry("birthday", selectedDate.value);
    if (!tksOpsCombi.value) {
      getProductGroup("alter", getFormattedDate(selectedDate.value));
    }
  }
);
watch(
  () => props.productGroup,
  () => {
    selectedProductGroupId.value = props.productGroup;
    getProductGroup(undefined, undefined, props.productGroupObject);
  }
);
if (props.tracking) {
  trackInitial(props.productGroup);
}
</script>

<style lang="scss">
$breakpoint-l: 996px;
$breakpoint-m: 747px;
$breakpoint-xs: 400px;

.quote-calculation {
  font-family: var(--font-open-sans-regular), sans-serif;
  color: var(--color-gray-700);
  display: grid;
  grid-template-areas: "a" "b" "c" "d";
  font-size: var(--font-size);

  @media screen and (min-width: $breakpoint-m) {
    grid-template-areas: "a a" "b c" "d d";
    grid-template-columns: minmax(350px, 34%) minmax(375px, 66%);
  }

  .agila-info-block {
    margin-bottom: 0;
  }

  .table-head,
  .table-head-bottom {
    &.hps-combi {
      grid-template-columns: 1fr 1fr;

      .table-head-column-con {
        border-right: 0 !important;
      }
    }

    &.single-product {
      grid-template-columns: 1fr;

      .table-head-column-con {
        border-right: 0 !important;
      }
    }

    @media screen and (max-width: $breakpoint-m - 1px) {
      border-bottom: 1px solid var(--color-gray-400);
      &.sticky {
        position: sticky;
        top: 0;
      }
    }
    @media screen and (min-width: $breakpoint-l) {
      grid-template-areas: "b c d";
    }

    .table-head-hint {
      grid-area: a;
      margin-bottom: 8px;

      &.display-none {
        display: none;
      }

      @media screen and (min-width: $breakpoint-l) {
        display: none;
      }
    }

    .table-head-column {
      border-radius: 16px 16px 0 0;
      padding: 0;
      text-align: center;
      align-self: end;
      overflow: hidden;

      .table-head-column-con {
        background-color: var(--color-blue-100);
        padding: 8px 8px 12px;
      }

      &.non-highlight-column {
        .table-head-column-con {
          background-color: var(--color-blue-50);
        }
      }

      &__recommendation {
        background: var(--color-blue-600);
        color: var(--color-white);
        text-align: center;
        font-size: 13px;
        padding: 2px 10px;

        @media screen and (min-width: $breakpoint-l) {
          &.mobile {
            display: none;
          }
        }

        @media screen and (max-width: $breakpoint-l - 1px) {
          &.desktop {
            display: none;
          }
        }
      }

      &__name {
        font-size: 13px;

        @media screen and (min-width: $breakpoint-l) {
          &.mobile {
            display: none;
          }
        }

        @media screen and (max-width: $breakpoint-l - 1px) {
          &.desktop {
            display: none;
          }
        }
      }

      &__price {
        font-family: var(--font-roboto-bold), sans-serif;
        padding: 6px 0 10px;
        text-align: center;

        @media screen and (min-width: $breakpoint-xs) {
          font-size: 20px;
        }

        span {
          font-family: var(--font-open-sans-regular), sans-serif;
          font-size: 13px;
        }

        @media screen and (max-width: $breakpoint-l - 1px) {
          &--ab {
            display: none;
          }
        }

        &.display-none {
          opacity: 0;
          min-height: 37px;
          @media screen and (min-width: $breakpoint-xs) {
            min-height: 42px;
          }
        }
      }

      .agila-info-block {
        height: 46px;
        width: 100%;
        box-sizing: border-box;
        display: flex;
        justify-content: center;
      }
    }

    .p-button {
      display: inline-block;
      width: 100%;

      @media screen and (min-width: $breakpoint-l) {
        .select-btn-mobile {
          display: none;
        }
      }

      @media screen and (max-width: $breakpoint-l - 1px) {
        .select-btn-desktop {
          display: none;
        }
      }

      .p-button-label {
        display: none;
      }
    }

    .table-head-column {
      border-left: 1px solid var(--color-gray-500);
      border-right: 1px solid var(--color-gray-500);
      border-top: 1px solid var(--color-gray-500);
    }

    > div:nth-of-type(3) {
      margin-left: -1px;
      margin-right: -1px;
    }

    .noBorderRadius {
      border-radius: 0 !important;
    }

    > div:nth-of-type(4) {
      grid-area: d;
    }
  }

  .table-head-bottom {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    border-bottom: 0;

    .table-head-column {
      border-radius: 0 0 16px 16px;
      border: 0;

      .table-head-column-con {
        border-radius: 0 0 16px 16px;
      }

      &:nth-of-type(2) {
        margin-left: -1px;
        margin-right: -1px;
        border-left: 1px solid var(--color-gray-500);
        border-right: 1px solid var(--color-gray-500);
        z-index: 1;
      }

      &:nth-of-type(3) {
        margin: 0;
      }
    }
  }
}
</style>
